import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import {
  AgentChatPanel,
  AgentChatProvider,
  useAgentChatContext,
} from '@/components/agent-chat';
import { AgentEventsSidebar } from '@/components/agents/agent-events-sidebar';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/routes';
import { useAgentRunEvents } from '@/hooks/use-agent-run-events';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import {
  type AgentConversationSummary,
  deleteAgentConversation,
  listAgentConversations,
} from '@/services/agents/config';
import type { AgentWorkflowEvent } from '@/services/agents/run-events-stream';
import { useOrgStore } from '@/stores/org-store';

export function AgentChatPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentOrg = useOrgStore((s) => s.currentOrg);

  const [conversationId, setConversationId] = useState<string | null>(
    searchParams.get('conversation') || null,
  );
  const [conversations, setConversations] = useState<
    AgentConversationSummary[]
  >([]);
  const {
    events: workflowEvents,
    activeRunCount,
    watchRun,
    reset: resetRunEvents,
  } = useAgentRunEvents({ orgId: currentOrg?.orgid, agentId });

  const refreshConversations = useCallback(async () => {
    if (!currentOrg?.orgid || !agentId) return;
    try {
      const list = await listAgentConversations(currentOrg.orgid, agentId);
      setConversations(list);
    } catch (e) {
      console.warn('Failed to load conversations:', e);
    }
  }, [currentOrg?.orgid, agentId]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  const handleConversationChange = useCallback(
    (id: string) => {
      setConversationId(id);
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set('conversation', id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectConversation = useCallback(
    (id: string) => {
      setConversationId(id);
      resetRunEvents();
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set('conversation', id);
          return next;
        },
        { replace: true },
      );
    },
    [resetRunEvents, setSearchParams],
  );

  const handleNewConversation = useCallback(() => {
    setConversationId(null);
    resetRunEvents();
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.delete('conversation');
        return next;
      },
      { replace: true },
    );
  }, [resetRunEvents, setSearchParams]);

  const handleDeleteConversation = useCallback(
    async (id: string): Promise<boolean> => {
      if (!currentOrg?.orgid || !agentId) return false;
      try {
        await deleteAgentConversation(currentOrg.orgid, agentId, id);
        const wasCurrent = conversationId === id;
        if (wasCurrent) {
          handleNewConversation();
        }
        refreshConversations();
        return wasCurrent;
      } catch (e) {
        console.warn('Failed to delete conversation:', e);
        return false;
      }
    },
    [
      currentOrg?.orgid,
      agentId,
      conversationId,
      handleNewConversation,
      refreshConversations,
    ],
  );

  return (
    <AgentChatProvider
      agentId={agentId ?? ''}
      conversationId={conversationId}
      onConversationChange={handleConversationChange}
      onRunStarted={watchRun}
      onTurnComplete={refreshConversations}
    >
      <AgentChatPageContent
        conversations={conversations}
        workflowEvents={workflowEvents}
        activeRunCount={activeRunCount}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
      />
    </AgentChatProvider>
  );
}

interface AgentChatPageContentProps {
  conversations: AgentConversationSummary[];
  workflowEvents: AgentWorkflowEvent[];
  activeRunCount: number;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => Promise<boolean>;
}

function AgentChatPageContent({
  conversations,
  workflowEvents,
  activeRunCount,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
}: AgentChatPageContentProps) {
  const navigate = useOrgNavigate();
  const {
    agent,
    agentLoading,
    conversationId,
    isSending,
    clearMessages,
    focusComposer,
  } = useAgentChatContext();

  const handleNewConversation = () => {
    onNewConversation();
    clearMessages();
    focusComposer();
  };

  const handleDeleteConversation = async (id: string) => {
    const resetToNew = await onDeleteConversation(id);
    if (resetToNew) {
      clearMessages();
      focusComposer();
    }
  };

  if (agentLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className='flex flex-col items-center justify-center h-full gap-3 text-center p-6'>
        <p className='text-sm text-muted-foreground'>Agent not found.</p>
        <Button variant='outline' onClick={() => navigate(ROUTES.AGENTS.ROOT)}>
          Back to agents
        </Button>
      </div>
    );
  }

  return (
    <div className='flex h-[calc(100vh-4rem)] w-full'>
      {/* Sidebar - conversations list */}
      <aside className='w-64 border-r border-border/60 flex flex-col bg-muted/20 shrink-0'>
        <div className='p-3 border-b border-border/60'>
          <Button
            size='sm'
            className='w-full justify-start'
            onClick={handleNewConversation}
          >
            <Plus className='h-4 w-4 mr-2' />
            New conversation
          </Button>
        </div>
        <div className='flex-1 overflow-y-auto'>
          {conversations.length === 0 ? (
            <p className='text-xs text-muted-foreground p-3'>
              No conversations yet.
            </p>
          ) : (
            <ul className='py-1'>
              {conversations.map((c) => (
                <li key={c.conversation_id}>
                  <button
                    type='button'
                    onClick={() => onSelectConversation(c.conversation_id)}
                    className={
                      'group w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-start justify-between gap-2 ' +
                      (c.conversation_id === conversationId
                        ? 'bg-accent/60'
                        : '')
                    }
                  >
                    <div className='min-w-0 flex-1'>
                      <p className='text-xs font-medium truncate'>
                        {c.preview || '(empty)'}
                      </p>
                      {c.last_message_at && (
                        <p className='text-[10px] text-muted-foreground mt-0.5'>
                          {new Date(c.last_message_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <button
                      type='button'
                      className='opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConversation(c.conversation_id);
                      }}
                      aria-label='Delete conversation'
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </button>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className='p-3 border-t border-border/60'>
          <Button
            variant='ghost'
            size='sm'
            className='w-full justify-start text-xs'
            onClick={() =>
              navigate(ROUTES.AGENTS.CONFIG.replace(':agentId', agent.id))
            }
          >
            Configure agent
          </Button>
        </div>
      </aside>

      {/* Main column */}
      <main className='flex-1 flex flex-col min-w-0'>
        <header className='flex items-center justify-between p-4 border-b border-border/60'>
          <div className='min-w-0'>
            <h1 className='text-base font-semibold truncate'>{agent.name}</h1>
            {agent.description && (
              <p className='text-xs text-muted-foreground truncate'>
                {agent.description}
              </p>
            )}
          </div>
          <span className='text-xs text-muted-foreground font-mono shrink-0 ml-3'>
            {agent.model_name}
          </span>
        </header>

        <AgentChatPanel agentId={agent.id} layout='full' />
      </main>

      <AgentEventsSidebar
        events={workflowEvents}
        streaming={isSending || activeRunCount > 0}
      />
    </div>
  );
}
